import { useMemo, useState } from 'react';
import {
  Table,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Button,
  Spin,
  Tooltip,
  message,
  Modal,
  Form,
  Popconfirm,
} from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getMarketIndicatorMappings,
  createMarketIndicatorMapping,
  updateMarketIndicatorMapping,
  deleteMarketIndicatorMapping,
} from '../../api/project_type';
import { getMarketIndicators } from '../../api/project';
import { getGHGStandards } from '../../api/ghg';

const { Text } = Typography;

// Categories where only one indicator can be selected at a time
const SINGLE_SELECT_CATEGORIES = new Set(['ccp', 'article_6']);

const MAPPINGS_QUERY_KEY = ['marketIndicatorMappings'];

type StatusType = 'mapped' | 'needs-mapping';

const NEW_ENTRY_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function isRecentlyCreated(createdAt: string | null | undefined) {
  if (!createdAt) return false;
  const createdTime = new Date(createdAt).getTime();
  if (Number.isNaN(createdTime)) return false;
  return Date.now() - createdTime <= NEW_ENTRY_WINDOW_MS;
}

const STAT_CARD_SOFT_BG: Record<string, string> = {
  '#8c8c8c': '#f4f4f4',
  '#52c41a': '#f0fbe8',
  '#ff4d4f': '#fff1f1',
  '#1677ff': '#eef5ff',
};

function StatCards({
  stats,
}: {
  stats: { label: string; value: number; color: string; active?: boolean; onClick?: () => void }[];
}) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
      {stats.map((stat) => (
        <div
          key={stat.label}
          onClick={stat.onClick}
          style={{
            flex: '1 1 128px',
            minWidth: 128,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            background: stat.active ? STAT_CARD_SOFT_BG[stat.color] || '#f5f5f5' : '#fff',
            borderTop: stat.active ? `1px solid ${stat.color}` : '1px solid #f0f0f0',
            borderRight: stat.active ? `1px solid ${stat.color}` : '1px solid #f0f0f0',
            borderBottom: stat.active ? `1px solid ${stat.color}` : '1px solid #f0f0f0',
            borderLeft: `3px solid ${stat.color}`,
            borderRadius: 8,
            padding: '7px 12px',
            cursor: stat.onClick ? 'pointer' : 'default',
            transition: 'background 0.15s, border-color 0.15s',
          }}
        >
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            {stat.label}
          </Text>
          <Text strong style={{ fontSize: 17, fontVariantNumeric: 'tabular-nums' }}>
            {stat.value}
          </Text>
        </div>
      ))}
    </div>
  );
}

function MarketIndicatorMapper() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | StatusType | 'new'>('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm();

  const { data: rawData = [], isLoading, isFetching } = useQuery({
    queryKey: MAPPINGS_QUERY_KEY,
    queryFn: async () => {
      const data = await getMarketIndicatorMappings();
      const rows = data?.response ?? (Array.isArray(data) ? data : []);
      return (rows as any[]).map((row) => ({
        ...row,
        market_indicators: Array.isArray(row.market_indicators) ? row.market_indicators : [],
      }));
    },
  });

  const { data: marketIndicatorsData, isLoading: indicatorsLoading } = useQuery({
    queryKey: ['marketIndicators'],
    queryFn: getMarketIndicators,
  });

const { data: ghgStandardsData } = useQuery({
    queryKey: ['ghgStandards'],
    queryFn: getGHGStandards,
  });

const ghgStandardOptions = useMemo(() => {
    const standards = ghgStandardsData?.response ?? (Array.isArray(ghgStandardsData) ? ghgStandardsData : []);
    return (standards as any[]).map((s) => ({ value: s.code, label: s.code }));
  }, [ghgStandardsData]);

  const marketIndicatorsRaw = useMemo(() => {
    const raw = Array.isArray(marketIndicatorsData) ? marketIndicatorsData : marketIndicatorsData?.response || [];
    return raw as any[];
  }, [marketIndicatorsData]);

  const indicatorsByName = useMemo(() => {
    const map: Record<string, any> = {};
    marketIndicatorsRaw.forEach((mi) => { if (mi.name) map[mi.name] = mi; });
    return map;
  }, [marketIndicatorsRaw]);

  const indicatorToCategoryName = useMemo(() => {
    const map: Record<string, string> = {};
    marketIndicatorsRaw.forEach((mi) => {
      map[mi.name] = mi.category?.name || mi.category_name || '';
    });
    return map;
  }, [marketIndicatorsRaw]);

  const groupedIndicatorOptions = useMemo(() => {
    const groups: Record<string, { name: string; display: string; items: { value: string; label: string }[] }> = {};
    marketIndicatorsRaw.forEach((mi) => {
      const catName = mi.category?.name || mi.category_name || '__uncategorized';
      const catDisplay = mi.category?.display_name || mi.category_display_name || mi.category?.name || mi.category_name || 'Other';
      if (!groups[catName]) groups[catName] = { name: catName, display: catDisplay, items: [] };
      groups[catName].items.push({ value: mi.name, label: mi.display_name });
    });
    return Object.values(groups)
      .map((g) => ({ ...g, items: g.items.sort((a, b) => a.label.localeCompare(b.label)) }))
      .sort((a, b) => a.display.localeCompare(b.display));
  }, [marketIndicatorsRaw]);

  const selectOptions = useMemo(() => {
    return groupedIndicatorOptions.map((g) => {
      const isRestricted = SINGLE_SELECT_CATEGORIES.has(g.name);
      return {
        label: (
          <span style={{ fontWeight: 600, color: '#595959' }}>
            {g.display}
            {isRestricted && (
              <Tag color="gold" style={{ marginLeft: 8, fontWeight: 400 }}>pick one</Tag>
            )}
          </span>
        ),
        title: g.display,
        options: g.items,
      };
    });
  }, [groupedIndicatorOptions]);

  const formatValue = (v: string) => {
    const mi = indicatorsByName[v];
    if (!mi) return v;
    const catDisplay = mi.category?.display_name || mi.category_display_name || 'Other';
    return `${catDisplay} - ${mi.display_name}`;
  };

  const { mutate: saveMapping, isLoading: isSaving } = useMutation(
    ({ record, marketIndicators }: { record: any; marketIndicators: string[] }) =>
      updateMarketIndicatorMapping(record.id, {
        project_id: record.project_id,
        ghg_standard_code: record.ghg_standard_code,
        market_indicators: marketIndicators,
      }),
    {
      onMutate: async ({ record, marketIndicators }) => {
        await queryClient.cancelQueries(MAPPINGS_QUERY_KEY);
        const previous = queryClient.getQueryData<any[]>(MAPPINGS_QUERY_KEY);
        queryClient.setQueryData<any[]>(MAPPINGS_QUERY_KEY, (old) =>
          (old || []).map((r) => {
            if (r.id !== record.id) return r;
            return {
              ...r,
              market_indicators: marketIndicators.map((name) => {
                const found = indicatorsByName[name];
                return found
                  ? { id: found.id, name: found.name, display_name: found.display_name }
                  : { name, display_name: name };
              }),
              market_indicator_ids: marketIndicators.map((name) => indicatorsByName[name]?.id ?? name),
            };
          }),
        );
        return { previous };
      },
      onError: (_err, _vars, ctx: any) => {
        if (ctx?.previous) queryClient.setQueryData(MAPPINGS_QUERY_KEY, ctx.previous);
        message.error('Failed to save mapping. Please try again.');
      },
      onSuccess: () => { message.success({ content: 'Mapping saved', duration: 1.2 }); },
      onSettled: () => queryClient.invalidateQueries(MAPPINGS_QUERY_KEY),
    },
  );

  const { mutate: createMapping, isLoading: isCreating } = useMutation(
    (data: any) => createMarketIndicatorMapping(data),
    {
      onSuccess: () => {
        message.success('Mapping created successfully');
        setCreateModalOpen(false);
        createForm.resetFields();
        queryClient.invalidateQueries(MAPPINGS_QUERY_KEY);
      },
      onError: (err: any) => {
        message.error(err?.message || 'Failed to create mapping');
      },
    },
  );

  const { mutate: deleteMapping, isLoading: isDeleting } = useMutation(
    (id: string) => deleteMarketIndicatorMapping(id),
    {
      onSuccess: () => {
        message.success('Mapping deleted');
        queryClient.invalidateQueries(MAPPINGS_QUERY_KEY);
      },
      onError: (err: any) => {
        message.error(err?.message || 'Failed to delete mapping');
      },
    },
  );

  const filteredRows = useMemo(() => {
    const text = search.toLowerCase().trim();
    return (rawData as any[]).filter((row) => {
      const isMapped = Array.isArray(row.market_indicators) && row.market_indicators.length > 0;
      if (statusFilter === 'new') {
        if (isMapped || !isRecentlyCreated(row.created_at)) return false;
      } else if (statusFilter === 'mapped' && !isMapped) {
        return false;
      } else if (statusFilter === 'needs-mapping' && isMapped) {
        return false;
      }
      if (!text) return true;
      const indicatorText = (row.market_indicators || [])
        .map((mi: any) => mi.display_name || mi.name || '')
        .join(' ');
      const haystack = [
        row.ghg_standard_code,
        row.project_id,
        row.methodology,
        row.methodology_version,
        indicatorText,
      ].join(' ').toLowerCase();
      return haystack.includes(text);
    });
  }, [rawData, search, statusFilter]);

  const handleIndicatorsChange = (record: any, values: string[]) => {
    const seenCategory: Record<string, boolean> = {};
    const next: string[] = [];
    for (let i = values.length - 1; i >= 0; i--) {
      const v = values[i];
      const cat = indicatorToCategoryName[v];
      if (cat && SINGLE_SELECT_CATEGORIES.has(cat)) {
        if (seenCategory[cat]) continue;
        seenCategory[cat] = true;
      }
      next.unshift(v);
    }
    if (next.length !== values.length) {
      message.info('Only one indicator can be selected in CCP and Article 6 categories.');
    }
    saveMapping({ record, marketIndicators: next });
  };

  const handleCreate = (values: any) => {
    createMapping({
      project_id: values.project_id,
      ghg_standard_code: values.ghg_standard_code,
      market_indicators: values.market_indicators || [],
    });
  };

  const columns: any[] = [
    {
      title: 'Project',
      key: 'project',
      width: 200,
      sorter: (a: any, b: any) => (a.project_id || '').localeCompare(b.project_id || ''),
      render: (_: any, record: any) => {
        const isUnmapped = !Array.isArray(record.market_indicators) || record.market_indicators.length === 0;
        return (
          <Space size={4}>
            <Text>{record.ghg_standard_code || record.project_id
              ? `${record.ghg_standard_code ?? ''} ${record.project_id ?? ''}`.trim()
              : <Text type="secondary">—</Text>}
            </Text>
            {isUnmapped && isRecentlyCreated(record.created_at) && <Tag color="blue">New</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Harmonized Methodology',
      dataIndex: 'methodology',
      key: 'methodology',
      width: 220,
      sorter: (a: any, b: any) => (a.methodology || '').localeCompare(b.methodology || ''),
      render: (value: string | null) =>
        value ? (
          <Tooltip title={value} placement="topLeft">
            <Text
              style={{
                display: 'inline-block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Version',
      dataIndex: 'methodology_version',
      key: 'methodology_version',
      width: 100,
      sorter: (a: any, b: any) => (a.methodology_version || '').localeCompare(b.methodology_version || ''),
      render: (value: string | null) => value || <Text type="secondary">—</Text>,
    },
    {
      title: 'Market labels',
      key: 'market_indicators',
      width: 380,
      sorter: (a: any, b: any) =>
        (a.market_indicators?.length ?? 0) - (b.market_indicators?.length ?? 0),
      render: (_: any, record: any) => {
        const selected = (record.market_indicators || []).map(
          (mi: any) => mi.name || mi.display_name || mi.id,
        );
        const flatKnownValues = new Set(
          groupedIndicatorOptions.flatMap((g) => g.items.map((i) => i.value)),
        );
        const extraOptions = selected
          .filter((v: string) => !flatKnownValues.has(v))
          .map((v: string) => ({ value: v, label: v }));
        const options =
          extraOptions.length > 0
            ? [...selectOptions, { label: 'Other', options: extraOptions }]
            : selectOptions;
        const allFormatted = selected.map(formatValue);
        return (
          <Select
            mode="multiple"
            allowClear
            placeholder="Select market labels"
            value={selected}
            options={options as any}
            onChange={(vals) => handleIndicatorsChange(record, vals || [])}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            maxTagCount="responsive"
            loading={indicatorsLoading}
            tagRender={({ value, closable, onClose }) => (
              <Tag closable={closable} onClose={onClose} style={{ marginInlineEnd: 4 }}>
                {formatValue(value as string)}
              </Tag>
            )}
            maxTagPlaceholder={(omitted) => (
              <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{allFormatted.join('\n')}</div>}>
                <span>+{omitted.length}…</span>
              </Tooltip>
            )}
          />
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      sorter: (a: any, b: any) => {
        const ma = Array.isArray(a.market_indicators) && a.market_indicators.length > 0 ? 0 : 1;
        const mb = Array.isArray(b.market_indicators) && b.market_indicators.length > 0 ? 0 : 1;
        return ma - mb;
      },
      render: (_: any, record: any) => {
        const mapped = Array.isArray(record.market_indicators) && record.market_indicators.length > 0;
        return mapped ? <Tag color="green">Mapped</Tag> : <Tag color="red">Needs Mapping</Tag>;
      },
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      render: (_: any, record: any) => (
        <Popconfirm
          title="Delete this mapping?"
          onConfirm={() => deleteMapping(record.id)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  const totalCount = (rawData as any[]).length;
  const needsMappingCount = (rawData as any[]).filter(
    (r) => !Array.isArray(r.market_indicators) || r.market_indicators.length === 0,
  ).length;
  const mappedCount = totalCount - needsMappingCount;
  const newCount = (rawData as any[]).filter((r) => {
    const isMapped = Array.isArray(r.market_indicators) && r.market_indicators.length > 0;
    return !isMapped && isRecentlyCreated(r.created_at);
  }).length;
  const loading = isLoading || isFetching || isSaving || isDeleting;

  return (
    <Spin spinning={loading} tip="Loading…">
      <StatCards
        stats={[
          {
            label: 'Total Projects',
            value: totalCount,
            color: '#8c8c8c',
            active: statusFilter === 'all',
            onClick: () => setStatusFilter('all'),
          },
          {
            label: 'Mapped',
            value: mappedCount,
            color: '#52c41a',
            active: statusFilter === 'mapped',
            onClick: () => setStatusFilter('mapped'),
          },
          {
            label: 'Needs Mapping',
            value: needsMappingCount,
            color: '#ff4d4f',
            active: statusFilter === 'needs-mapping',
            onClick: () => setStatusFilter('needs-mapping'),
          },
          {
            label: 'New',
            value: newCount,
            color: '#1677ff',
            active: statusFilter === 'new',
            onClick: () => setStatusFilter('new'),
          },
        ]}
      />
      <div style={{ margin: '0 0 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search project, GHG standard…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360, flex: '1 1 240px' }}
          allowClear
        />
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => queryClient.invalidateQueries(MAPPINGS_QUERY_KEY)}
            loading={isFetching && !isLoading}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            Create Mapping
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={filteredRows}
        rowKey="id"
        size="middle"
        tableLayout="fixed"
        showSorterTooltip={false}
        pagination={{ defaultPageSize: 50, showSizeChanger: true }}
      />
      <Modal
        title="Create Market labels Mapping"
        open={createModalOpen}
        onCancel={() => { setCreateModalOpen(false); createForm.resetFields(); }}
        onOk={() => createForm.submit()}
        confirmLoading={isCreating}
        okText="Create"
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="project_id"
            label="Project ID"
            rules={[{ required: true, message: 'Please enter a project ID' }]}
          >
            <Input placeholder="Enter project ID" allowClear />
          </Form.Item>
          <Form.Item
            name="ghg_standard_code"
            label="GHG Standard"
            rules={[{ required: true, message: 'Please select a GHG standard' }]}
          >
            <Select
              showSearch
              placeholder="Select GHG standard"
              options={ghgStandardOptions}
              optionFilterProp="label"
              allowClear
            />
          </Form.Item>
          <Form.Item name="market_indicators" label="Market labels">
            <Select
              mode="multiple"
              placeholder="Select market labels"
              options={selectOptions as any}
              showSearch
              optionFilterProp="label"
              loading={indicatorsLoading}
              allowClear
              maxTagCount="responsive"
              tagRender={({ value, closable, onClose }) => (
                <Tag closable={closable} onClose={onClose} style={{ marginInlineEnd: 4 }}>
                  {formatValue(value as string)}
                </Tag>
              )}
              maxTagPlaceholder={(omitted) => (
                <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{omitted.map((o) => formatValue(o.value as string)).join('\n')}</div>}>
                  <span>+{omitted.length}…</span>
                </Tooltip>
              )}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Spin>
  );
}

export default MarketIndicatorMapper;
